import { apiService } from './api';

/**
 * Projects service for handling project-related API calls
 */
export const projectsService = {
  /**
   * Get all projects for the authenticated user
   * @returns {Promise<Object>} Projects response with docs array
   */
  getProjects: async () => {
    const response = await apiService.get('/projects');
    return response;
  },

  /**
   * Get a specific project by ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project response
   */
  getProject: async projectId => {
    return await apiService.get(`/projects/${projectId}`);
  },

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @param {string} projectData.name - Project name
   * @param {string} [projectData.description] - Project description
   * @returns {Promise<Object>} Created project response
   */
  createProject: async projectData => {
    return await apiService.post('/projects', projectData);
  },

  /**
   * Update an existing project
   * @param {string} projectId - Project ID
   * @param {Object} projectData - Updated project data
   * @returns {Promise<Object>} Updated project response
   */
  updateProject: async (projectId, projectData) => {
    return await apiService.put(`/projects/${projectId}`, projectData);
  },

  /**
   * Delete a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Delete response
   */
  deleteProject: async projectId => {
    return await apiService.delete(`/projects/${projectId}`);
  },

  /**
   * Get project analytics with date filters
   * @param {string} projectId - Project ID
   * @param {Object} filters - Date filter options
   * @param {string} [filters.startDate] - Start date (ISO string)
   * @param {string} [filters.endDate] - End date (ISO string)
   * @returns {Promise<Object>} Analytics response with time series data
   */
  getProjectAnalytics: async (projectId, filters = {}) => {
    const params = new URLSearchParams({
      projectId,
      ...filters,
    }).toString();
    return await apiService.get(`/projects/${projectId}/analytics?${params}`);
  },
};

export default projectsService;
